import React, { useState, useEffect } from 'react';
import { Zap, Flame, Mail, FileText, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnergySavingsCalculator = () => {
  const [electricZip, setElectricZip] = useState('');
  const [electricUsage, setElectricUsage] = useState('');
  const [gasZip, setGasZip] = useState('');
  const [gasUsage, setGasUsage] = useState('');
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [currentProposal, setCurrentProposal] = useState('');
  
  // Rate data fetched from backend (single source of truth)
  const [rates, setRates] = useState(null);

  // Fetch rates from backend on component mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/rates`);
        const data = await response.json();
        setRates(data);
      } catch (error) {
        console.error('Failed to fetch rates:', error);
        // Fallback to hardcoded rates if API fails
        setRates({
          electricity: {
            current: { PA: 0.12114, NY: 0.1004, NJ: 0.15786, IL: 0.10128, MA: 0.1324, CT: 0.1119, MD: 0.12021, NH: 0.1324, OH: 0.0952, TX: 0.09, FL: 0.1188 },
            offer: { PA: 0.10555, NY: 0.08162, NJ: 0.13745, IL: 0.08178, MA: 0.11858, CT: 0.10599, MD: 0.11203, NH: 0.10207, OH: 0.08084, TX: 0.072, FL: 0.095 }
          },
          gas: {
            current: { FL: 0.80, NY: 0.985, CT: 1.05, MA: 1.10, NJ: 1.058, GA: 1.00, TX: 0.60, OH: 0.6454, PA: 0.4949, IL: 0.54 },
            offer: { FL: 0.65067, NY: 0.869, CT: 0.92, MA: 0.819, NJ: 0.52567, GA: 0.85, TX: 0.48, OH: 0.6084, PA: 0.3025, IL: 0.492 }
          }
        });
      } finally {
        // rates loaded
      }
    };
    fetchRates();
  }, []);
  const generateProposal = async (type) => {
    const zip = type === 'electric' ? electricZip : gasZip;
    const usage = parseFloat(type === 'electric' ? electricUsage : gasUsage);

    if (!zip || !usage) {
      alert('Please enter both ZIP code and usage.');
      return;
    }

    if (!rates) {
      alert('Rate data is loading. Please try again in a moment.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      const data = await response.json();
      const state = data.places[0]['state abbreviation'];

      // Use rates from backend (single source of truth)
      let inc = type === 'electric' ? rates.electricity.current[state] : rates.gas.current[state];
      let off = type === 'electric' ? rates.electricity.offer[state] : rates.gas.offer[state];

      if (!inc) {
        alert('No rate data available for this ZIP code.');
        setLoading(false);
        return;
      }

      if (!off) off = (inc * 0.8).toFixed(4);

      const save = ((inc - off) / inc * 100).toFixed(2);
      const annual = ((inc - off) * usage).toFixed(2);
      const direct = (off * 1.05).toFixed(4);
      const hudson = (off * 1.07).toFixed(4);
      const premium = (off * 1.10).toFixed(4);

      const proposalText = `Proposal for ${state} | Current $${inc} | Offer $${off} | Savings ${save}% ($${annual})`;
      setCurrentProposal(proposalText);

      setProposal({
        type,
        state,
        currentRate: inc,
        offerRate: off,
        usage,
        savingsPercent: save,
        annualSavings: annual,
        suppliers: [
          { name: 'Premium Supplier', rate: off },
          { name: 'Premium Supplier', rate: direct },
          { name: 'Premium Supplier', rate: hudson },
          { name: 'Premium Supplier', rate: premium }
        ]
      });
    } catch {
      alert('ZIP code lookup failed. Please try again.');
    }
    setLoading(false);
  };

  const handleEmailProposal = () => {
    window.location.href = `mailto:sales@lastmileadvisors.com?subject=Your Custom Energy Proposal&body=${encodeURIComponent(currentProposal)}`;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    
    // Build the contract data object
    const contractData = {
      name: formData.get('authorized_signer') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      business_name: formData.get('business_name') || '',
      account_number: formData.get('account_number') || '',
      annual_usage: formData.get('annual_volume') || '',
      energy_type: proposal?.type || 'electric',
      state: proposal?.state || '',
      current_rate: proposal?.currentRate?.toString() || '',
      offer_rate: proposal?.offerRate?.toString() || '',
      savings_percent: proposal?.savingsPercent?.toString() || '',
      annual_savings: proposal?.annualSavings?.toString() || '',
      proposal_details: currentProposal,
      source: 'Homepage Calculator',
      session_id: `calc-${Date.now()}`
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/submit-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData),
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          const thankYouEl = document.getElementById('thankYouMessage');
          if (thankYouEl) {
            thankYouEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else {
        alert('There was an issue submitting your request. Please try again or email us directly at sales@lastmileadvisors.com');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an issue submitting your request. Please try again or email us directly at sales@lastmileadvisors.com');
    }
  };

  return (
    <div className="calculator-section">
      {/* Calculator Cards */}
      <div className="calculator-grid">
        {/* Electricity Calculator */}
        <Card className="calculator-card">
          <CardContent className="calculator-card-content">
            <div className="calculator-header">
              <Zap size={28} className="calculator-icon electric" />
              <h3 className="calculator-title">Electricity Savings</h3>
            </div>
            <div className="calculator-form">
              <div className="calculator-field">
                <Label htmlFor="electricZip">ZIP Code</Label>
                <Input
                  id="electricZip"
                  type="text"
                  placeholder="Enter ZIP Code"
                  value={electricZip}
                  onChange={(e) => setElectricZip(e.target.value)}
                  className="calculator-input"
                />
              </div>
              <div className="calculator-field">
                <Label htmlFor="electricUsage">Annual Usage (kWh)</Label>
                <Input
                  id="electricUsage"
                  type="number"
                  placeholder="Annual kWh"
                  value={electricUsage}
                  onChange={(e) => setElectricUsage(e.target.value)}
                  className="calculator-input"
                />
              </div>
              <Button
                onClick={() => generateProposal('electric')}
                className="calculator-btn"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Generate Proposal
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Natural Gas Calculator */}
        <Card className="calculator-card">
          <CardContent className="calculator-card-content">
            <div className="calculator-header">
              <Flame size={28} className="calculator-icon gas" />
              <h3 className="calculator-title">Natural Gas Savings</h3>
            </div>
            <div className="calculator-form">
              <div className="calculator-field">
                <Label htmlFor="gasZip">ZIP Code</Label>
                <Input
                  id="gasZip"
                  type="text"
                  placeholder="Enter ZIP Code"
                  value={gasZip}
                  onChange={(e) => setGasZip(e.target.value)}
                  className="calculator-input"
                />
              </div>
              <div className="calculator-field">
                <Label htmlFor="gasUsage">Annual Usage (Therms)</Label>
                <Input
                  id="gasUsage"
                  type="number"
                  placeholder="Annual Therms"
                  value={gasUsage}
                  onChange={(e) => setGasUsage(e.target.value)}
                  className="calculator-input"
                />
              </div>
              <Button
                onClick={() => generateProposal('gas')}
                className="calculator-btn"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Generate Proposal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Proposal Results */}
      {proposal && (
        <div className="proposal-results">
          <Card className="proposal-card">
            <CardContent className="proposal-content">
              <h3 className="proposal-title">
                {proposal.type === 'electric' ? <Zap size={24} className="inline mr-2" /> : <Flame size={24} className="inline mr-2" />}
                Proposal Summary for {proposal.state}
              </h3>
              
              <div className="proposal-details">
                <div className="proposal-item">
                  <span className="proposal-label">Current Rate:</span>
                  <span className="proposal-value">${proposal.currentRate}</span>
                </div>
                <div className="proposal-item">
                  <span className="proposal-label">Our Rate:</span>
                  <span className="proposal-value highlight">${proposal.offerRate}</span>
                </div>
                <div className="proposal-item">
                  <span className="proposal-label">Annual Usage:</span>
                  <span className="proposal-value">{proposal.usage.toLocaleString()} {proposal.type === 'electric' ? 'kWh' : 'Therms'}</span>
                </div>
                <div className="proposal-item savings">
                  <span className="proposal-label">Your Savings:</span>
                  <span className="proposal-value savings-value">{proposal.savingsPercent}% (${proposal.annualSavings}/year)</span>
                </div>
              </div>

              <div className="supplier-comparison">
                <h4 className="supplier-title">Supplier Rate Comparison</h4>
                <div className="supplier-grid">
                  {proposal.suppliers.map((supplier, index) => (
                    <div key={index} className="supplier-card">
                      <span className="supplier-name">{supplier.name}</span>
                      <span className="supplier-rate">${supplier.rate}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="proposal-actions">
                <Button onClick={handleEmailProposal} variant="outline" className="proposal-btn">
                  <Mail size={18} className="mr-2" />
                  Email Proposal
                </Button>
                <Button onClick={() => setShowContractForm(true)} className="proposal-btn primary">
                  <FileText size={18} className="mr-2" />
                  Request Contract for Review
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contract Request Form */}
      {showContractForm && (
        <div className="contract-form-section" id="contractForm">
          <Card className="contract-card">
            <CardContent className="contract-content">
              <h3 className="contract-title">
                <FileText size={24} className="inline mr-2" />
                Request a Contract for Review
              </h3>
              
              {!formSubmitted ? (
                <form
                  onSubmit={handleFormSubmit}
                  className="contract-form"
                >
                  <input type="hidden" name="calculated_proposal" value={currentProposal} />

                  <div className="form-grid">
                    <div className="form-field">
                      <Label htmlFor="business_name">Business Name</Label>
                      <Input id="business_name" name="business_name" placeholder="Business Name" required />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="authorized_signer">Authorized Contact Name</Label>
                      <Input id="authorized_signer" name="authorized_signer" placeholder="Authorized Contact Name" required />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" name="email" type="email" placeholder="Your Email Address" required />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" name="phone" type="tel" placeholder="Your Phone Number" />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="account_number">Utility Bill Account Number</Label>
                      <Input id="account_number" name="account_number" placeholder="Utility Bill Account Number" />
                    </div>
                    <div className="form-field">
                      <Label htmlFor="annual_volume">Annual Usage (kWh or Therms)</Label>
                      <Input id="annual_volume" name="annual_volume" type="number" placeholder="Annual Usage" />
                    </div>
                  </div>

                  <div className="file-uploads">
                    <div className="file-field">
                      <Label htmlFor="electric_bill">Upload Electric Bill</Label>
                      <Input id="electric_bill" name="electric_bill" type="file" multiple className="file-input" />
                    </div>
                    <div className="file-field">
                      <Label htmlFor="gas_bill">Upload Gas Bill</Label>
                      <Input id="gas_bill" name="gas_bill" type="file" multiple className="file-input" />
                    </div>
                  </div>

                  <Button type="submit" className="submit-btn">
                    <FileText size={18} className="mr-2" />
                    Send Contract Request
                  </Button>
                </form>
              ) : (
                <div id="thankYouMessage" className="thank-you-message">
                  <CheckCircle size={48} className="thank-you-icon" />
                  <p>Thank you! Your contract request has been sent successfully.</p>
                </div>
              )}

              <iframe name="hidden_iframe" style={{ display: 'none' }} title="hidden"></iframe>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EnergySavingsCalculator;
